import { z } from 'zod'

// ThemeProfile (themes studio): a named, shareable recipe for a person's look.
// Every value is typed: colours are hex, numbers have hard ranges, enums are
// closed, so nothing here can carry raw CSS. The Go twin is
// internal/kinds/themeprofile; testdata/themes/*.json is run by both suites.

export const COLOUR_RE = /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/
export const BLOB_RE = /^[0-9a-f]{64}$/

export const THEME_BASES = ['midnight', 'graphite', 'forest', 'dusk', 'ember', 'halloween', 'paper'] as const
export const STUDIO_BACKDROPS = ['dusk', 'drift', 'aurora', 'night', 'tide', 'lines', 'haunt'] as const
export const THEME_SCHEMES = ['auto', 'light', 'dark', 'black'] as const
export const STUDIO_MODES = ['light', 'dark', 'black'] as const
export type StudioMode = (typeof STUDIO_MODES)[number]

/** the close-button glyphs a theme may name; the same list as `CloseGlyphs` in internal/kinds/themeprofile */
export const CLOSE_GLYPHS = ['x', 'pumpkin'] as const
export type CloseGlyphKind = (typeof CLOSE_GLYPHS)[number]
/** the nav logos a theme may name; the same list as `LogoGlyphs` in internal/kinds/themeprofile */
export const LOGO_GLYPHS = ['mark', 'pumpkin'] as const
export type LogoKind = (typeof LOGO_GLYPHS)[number]
/** the scroll indicators a theme may name; the same list as `ScrollIndicators` in internal/kinds/themeprofile */
export const SCROLL_INDICATORS = ['none', 'witch'] as const
export type ScrollIndicatorKind = (typeof SCROLL_INDICATORS)[number]

/** the newest ThemeSpec version this build can draw; a newer one is kept as given and not applied */
export const THEME_SPEC_V = 1
/** limits shared with the server (internal/kinds/themeprofile) */
export const MAX_SPEC_BYTES = 8192
export const MAX_PROFILES_PER_AUTHOR = 40

export const MAX_IMAGES = 3

export const NAME_MAX = 40

/** user-perceived characters, as the server counts them (a ZWJ family is one) */
const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
export const graphemes = (s: string): number => [...segmenter.segment(s)].length

export const colour = z.string().regex(COLOUR_RE)

export const surfacesSpec = z
  .object({
    background: colour.optional(),
    raised: colour.optional(),
    sunken: colour.optional(),
    hairline: colour.optional(),
    text: colour.optional(),
    muted: colour.optional(),
    faint: colour.optional(),
  })
  .strict()

export const accentSpec = z
  .object({
    colour: colour.optional(),
    highlight: colour.optional(),
    ok: colour.optional(),
    warn: colour.optional(),
    danger: colour.optional(),
    info: colour.optional(),
  })
  .strict()

export const backgroundSpec = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('solid') }).strict(),
  z
    .object({
      kind: z.literal('gradient'),
      from: colour,
      to: colour,
      via: colour.optional(),
      angle: z.number().min(0).max(360).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('image'),
      blob: z.string().regex(BLOB_RE),
      fit: z.enum(['cover', 'contain']).optional(),
      dim: z.number().min(0).max(80).optional(),
      blur: z.number().min(0).max(24).optional(),
    })
    .strict(),
  z.object({ kind: z.literal('backdrop'), id: z.enum(STUDIO_BACKDROPS) }).strict(),
])

const modeFields = {
  surfaces: surfacesSpec.optional(),
  accent: accentSpec.optional(),
  background: backgroundSpec.optional(),
}
export const modeSpec = z.object(modeFields).strict()
/** black only: `trueBlack: false` lets black be tinted; unset keeps it #000000 */
export const blackModeSpec = z.object({ ...modeFields, trueBlack: z.boolean().optional() }).strict()

export const themeSpecV1 = z
  .object({
    v: z.literal(1),
    base: z.enum(THEME_BASES).optional(),
    scheme: z.enum(THEME_SCHEMES).optional(),
    modes: z
      .object({ light: modeSpec.optional(), dark: modeSpec.optional(), black: blackModeSpec.optional() })
      .strict()
      .optional(),
    accent: accentSpec.optional(),
    background: backgroundSpec.optional(),
    glass: z
      .object({
        on: z.boolean().optional(),
        strength: z.number().min(0).max(100).optional(),
        blur: z.number().min(0).max(40).optional(),
      })
      .strict()
      .optional(),
    shape: z
      .object({
        radius: z.number().min(0).max(24).optional(),
        border: z.number().min(0).max(3).optional(),
        hairline: z.number().min(0).max(3).optional(),
      })
      .strict()
      .optional(),
    space: z
      .object({
        density: z.enum(['compact', 'cosy', 'roomy']).optional(),
        spacing: z.number().min(0.85).max(1.25).optional(),
      })
      .strict()
      .optional(),
    type: z
      .object({
        size: z.number().min(0.9).max(1.2).optional(),
        weight: z.union([z.literal(400), z.literal(500), z.literal(600)]).optional(),
      })
      .strict()
      .optional(),
    motion: z.enum(['full', 'reduced', 'none']).optional(),
    icons: z
      .object({
        tint: z.union([z.enum(['none', 'accent', 'highlight']), colour]).optional(),
        strength: z.number().min(0).max(100).optional(),
        /** the glyph on a sheet's close button; unset keeps the X */
        close: z.enum(CLOSE_GLYPHS).optional(),
        /** the logo in the nav; unset takes the base palette's, else the mark */
        logo: z.enum(LOGO_GLYPHS).optional(),
        /** the scroll indicator; unset takes the base palette's, else none */
        scroll: z.enum(SCROLL_INDICATORS).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((s) => imagesOf(s).length <= MAX_IMAGES, 'at most 3 picture backgrounds')

/** a theme newer than this build knows: kept as given, never drawn */
const themeNewer = z.object({ v: z.number().int().min(2) }).passthrough()
export const themeSpec = z.union([themeSpecV1, themeNewer])

/** the blob ids a theme's picture backgrounds name */
export const imagesOf = (t: {
  background?: { kind: string; blob?: string }
  modes?: Record<string, { background?: { kind: string; blob?: string } } | undefined>
}): string[] =>
  [t.background, ...Object.values(t.modes ?? {}).map((m) => m?.background)].flatMap((b) =>
    b?.kind === 'image' && b.blob ? [b.blob] : [],
  )

export type Colour = z.infer<typeof colour>
export type ThemeSpec = z.infer<typeof themeSpecV1>
export type ModeSpec = z.infer<typeof blackModeSpec>
export type Background = z.infer<typeof backgroundSpec>
export type AccentSpec = z.infer<typeof accentSpec>
export type SurfacesSpec = z.infer<typeof surfacesSpec>

export const profileSpec = z
  .object({
    /** the profile's name; the envelope's `name` is derived from it (displayName) like every kind */
    name: z
      .string()
      .trim()
      .refine((n) => graphemes(n) >= 1 && graphemes(n) <= NAME_MAX, 'a name is 1 to 40 characters'),
    emoji: z
      .string()
      .trim()
      .refine(
        (e) => graphemes(e) === 1 && new TextEncoder().encode(e).length <= 32,
        'an emoji is one character',
      ),
    author_name: z.string().max(40).default(''),
    visibility: z.enum(['workspace', 'private']).default('workspace'),
    copied_from: z.string().uuid().optional(),
    theme: themeSpec,
  })
  .strict()
  .refine((p) => JSON.stringify(p).length <= MAX_SPEC_BYTES, 'a theme is at most 8 KB')
/** `theme` is a ThemeSpec v1 for this build; a newer one is kept as given and `canDraw` (lib/themeStudio.ts) says no */
export type ProfileSpec = Omit<z.infer<typeof profileSpec>, 'theme'> & { theme: ThemeSpec }

export const profileStatus = z.object({})

export const PROFILE_AUTHOR_LABEL = 'author'
export const OWNER_AUTHOR = 'owner'
